import { Box, Button, Text, VStack } from '@chakra-ui/react'
import React, { useCallback, useEffect, useMemo, useState } from 'react'

import {
  Checkbox,
  createTranslator,
  Modal,
  type TranslateFn,
} from '@mangovoice/mango-ui'

export interface PhonePermissionGroupOption {
  id: number
  name: string
  locked?: boolean
}

export interface EditPhonePermissionsDialogProps {
  isOpen: boolean
  onClose: () => void
  userName: string
  pbxName: string
  groups: PhonePermissionGroupOption[]
  initialSelectedGroupIds: number[]
  isSubmitting?: boolean
  onSubmit: (selectedGroupIds: number[]) => void
  t?: TranslateFn
}

function mergeWithLockedIds(
  selectedIds: number[],
  groups: PhonePermissionGroupOption[],
): number[] {
  const next = new Set(selectedIds)
  for (const group of groups) {
    if (group.locked) {
      next.add(group.id)
    }
  }
  return Array.from(next)
}

export const EditPhonePermissionsDialog: React.FC<
  EditPhonePermissionsDialogProps
> = ({
  isOpen,
  onClose,
  userName,
  pbxName,
  groups,
  initialSelectedGroupIds,
  isSubmitting = false,
  onSubmit,
  t,
}) => {
  const tr = useMemo(() => createTranslator(t), [t])
  const [selectedGroupIds, setSelectedGroupIds] = useState<number[]>([])

  useEffect(() => {
    if (!isOpen) return
    setSelectedGroupIds(mergeWithLockedIds(initialSelectedGroupIds, groups))
  }, [groups, initialSelectedGroupIds, isOpen])

  const selectedSet = useMemo(
    () => new Set(selectedGroupIds),
    [selectedGroupIds],
  )

  const handleToggle = useCallback((group: PhonePermissionGroupOption) => {
    if (group.locked) return
    setSelectedGroupIds((prev) => {
      const next = new Set(prev)
      if (next.has(group.id)) {
        next.delete(group.id)
      } else {
        next.add(group.id)
      }
      return Array.from(next)
    })
  }, [])

  const handleSubmit = useCallback(() => {
    onSubmit(mergeWithLockedIds(selectedGroupIds, groups))
  }, [groups, onSubmit, selectedGroupIds])

  return (
    <Modal
      open={isOpen}
      onOpenChange={(details) => {
        if (!details.open) onClose()
      }}
      title={tr(
        'users.editPhonePermissions.title',
        `Edit Phone Permissions: ${userName}`,
        { userName },
      )}
      titleFontSize="2xl"
      maxWidth="760px"
      closeOnEscape={!isSubmitting}
      footer={
        <>
          <Button
            variant="outline"
            colorPalette="neutral"
            onClick={onClose}
            disabled={isSubmitting}
          >
            {tr('users.editPhonePermissions.close', 'Close')}
          </Button>
          <Button
            colorPalette="brand"
            onClick={handleSubmit}
            loading={isSubmitting}
            disabled={isSubmitting}
          >
            {tr(
              'users.editPhonePermissions.submit',
              'Update Phone Permissions',
            )}
          </Button>
        </>
      }
    >
      <VStack align="stretch" gap={4}>
        <Text fontSize="2xl" fontWeight="semibold">
          {tr('users.editPhonePermissions.pbxLabel', `PBX: ${pbxName}`, {
            pbxName,
          })}
        </Text>

        <Box>
          <Text fontSize="md" fontWeight="semibold" mb={2}>
            {tr('users.editPhonePermissions.groups', 'Groups')}
          </Text>
          {groups.length === 0 ? (
            <Text fontSize="sm" color="fg.muted">
              {tr(
                'users.editPhonePermissions.noGroups',
                'No groups available for this PBX.',
              )}
            </Text>
          ) : (
            <Box
              p={2}
              borderWidth="1px"
              borderColor="border.default"
              bg="bg.default"
              borderRadius="md"
              boxShadow="sm"
            >
              <VStack align="stretch" gap={1}>
                {groups.map((group) => {
                  const isChecked = selectedSet.has(group.id)
                  return (
                    <Box
                      key={group.id}
                      px={2}
                      py={1.5}
                      borderRadius="sm"
                      bg={isChecked ? 'bg.subtle' : 'transparent'}
                      opacity={group.locked ? 0.85 : 1}
                      _hover={{
                        bg: group.locked
                          ? isChecked
                            ? 'bg.subtle'
                            : 'transparent'
                          : 'bg.subtle',
                      }}
                    >
                      <Checkbox
                        checked={isChecked}
                        disabled={group.locked}
                        onCheckedChange={() => handleToggle(group)}
                        size="sm"
                      >
                        <Text
                          as="span"
                          fontSize="sm"
                          fontWeight="medium"
                          color={group.locked ? 'fg.muted' : 'fg.default'}
                        >
                          {group.name}
                        </Text>
                        {group.locked && (
                          <Text as="span" color="fg.muted" fontSize="xs" ml={2}>
                            {tr(
                              'users.editPhonePermissions.required',
                              '(required)',
                            )}
                          </Text>
                        )}
                      </Checkbox>
                    </Box>
                  )
                })}
              </VStack>
              <Text fontSize="xs" color="fg.muted" px={1} pt={1}>
                {tr(
                  'users.editPhonePermissions.selectedCount',
                  `${selectedSet.size} of ${groups.length} selected`,
                  {
                    selected: selectedSet.size,
                    total: groups.length,
                  },
                )}
              </Text>
            </Box>
          )}
        </Box>
      </VStack>
    </Modal>
  )
}
